import React from 'react';
import {
  BriefcaseIcon,
  MapPinIcon,
  ClockIcon,
  AcademicCapIcon,
  HeartIcon,
  StarIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const Careers: React.FC = () => {
  const jobOpenings = [
    {
      id: '1',
      title: 'Senior Safari Guide',
      location: 'Nairobi, Kenya',
      type: 'Full-time',
      experience: '5+ years',
      salary: 'Competitive',
      description: 'Lead safari tours and provide exceptional wildlife experiences to our guests.',
      requirements: [
        'Professional safari guide certification',
        '5+ years of safari guiding experience',
        'Fluent in English and Swahili',
        'First aid certification',
        'Passion for wildlife conservation'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Professional development opportunities',
        'Travel allowances',
        'Flexible schedule'
      ]
    },
    {
      id: '2',
      title: 'Cultural Experience Coordinator',
      location: 'Arusha, Tanzania',
      type: 'Full-time',
      experience: '3+ years',
      salary: 'Competitive',
      description: 'Coordinate cultural experiences and community engagement activities.',
      requirements: [
        'Degree in tourism or related field',
        '3+ years in cultural tourism',
        'Strong communication skills',
        'Knowledge of local cultures',
        'Community engagement experience'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Cultural immersion opportunities',
        'Community development impact',
        'Flexible work environment'
      ]
    },
    {
      id: '3',
      title: 'Wildlife Photographer',
      location: 'Masai Mara, Kenya',
      type: 'Contract',
      experience: '2+ years',
      salary: 'Project-based',
      description: 'Capture stunning wildlife photography for our marketing and guest experiences.',
      requirements: [
        'Professional photography portfolio',
        '2+ years wildlife photography experience',
        'Own professional camera equipment',
        'Knowledge of wildlife behavior',
        'Creative vision and technical skills'
      ],
      benefits: [
        'Project-based compensation',
        'Access to prime wildlife locations',
        'Portfolio building opportunities',
        'Flexible schedule',
        'Creative freedom'
      ]
    },
    {
      id: '4',
      title: 'Customer Service Representative',
      location: 'Nairobi, Kenya',
      type: 'Full-time',
      experience: '2+ years',
      salary: 'Competitive',
      description: 'Provide excellent customer service and support to our guests.',
      requirements: [
        '2+ years customer service experience',
        'Excellent communication skills',
        'Multilingual preferred',
        'Tourism industry knowledge',
        'Problem-solving abilities'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Customer service training',
        'Career advancement opportunities',
        'Team collaboration'
      ]
    }
  ];

  const benefits = [
    {
      icon: HeartIcon,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance and wellness programs for all employees.'
    },
    {
      icon: AcademicCapIcon,
      title: 'Professional Development',
      description: 'Continuous learning opportunities and skill development programs.'
    },
    {
      icon: StarIcon,
      title: 'Work-Life Balance',
      description: 'Flexible schedules and time off to enjoy the beautiful East African landscapes.'
    },
    {
      icon: BriefcaseIcon,
      title: 'Career Growth',
      description: 'Clear career progression paths and advancement opportunities.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Join Our Team</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Be part of our mission to provide authentic African safari experiences while supporting wildlife conservation and local communities.
          </p>
        </div>
      </div>

      {/* Why Work With Us */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Team Image */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Safari team collaboration"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/60 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-3xl font-bold mb-2">Join Our Safari Family</h2>
              <p className="text-xl">Be part of Africa's premier safari experience</p>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-primary-900 mb-4">Why Work With Maasai SightTrails?</h2>
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            Join a team that's passionate about wildlife conservation, cultural preservation, and creating unforgettable experiences for our guests.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <benefit.icon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-primary-900 mb-3">{benefit.title}</h3>
              <p className="text-neutral-600">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Job Openings */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-primary-900 mb-8 text-center">Current Openings</h2>
          <div className="space-y-6">
            {jobOpenings.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-primary-900 mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-neutral-600">
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-1 text-primary-600" />
                        {job.location}
                      </div>
                      <div className="flex items-center">
                        <BriefcaseIcon className="h-4 w-4 mr-1 text-primary-600" />
                        {job.type}
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1 text-primary-600" />
                        {job.experience}
                      </div>
                      <div className="text-primary-600 font-medium">{job.salary}</div>
                    </div>
                  </div>
                  <button className="mt-4 md:mt-0 bg-primary-800 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200">
                    Apply Now
                  </button>
                </div>

                <p className="text-neutral-600 mb-4">{job.description}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-2">Requirements</h4>
                    <ul className="space-y-1">
                      {job.requirements.map((req, index) => (
                        <li key={index} className="flex items-start text-sm text-neutral-600">
                          <CheckIcon className="h-4 w-4 mr-2 text-primary-600 mt-0.5 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-2">Benefits</h4>
                    <ul className="space-y-1">
                      {job.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start text-sm text-neutral-600">
                          <CheckIcon className="h-4 w-4 mr-2 text-primary-600 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Application Process */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200">
          <h2 className="text-2xl font-bold text-primary-900 mb-6 text-center">How to Apply</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-primary-900 mb-2">Submit Application</h3>
              <p className="text-neutral-600">Send your CV and cover letter to sighttrailsafaris@gmail.com</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-primary-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-primary-900 mb-2">Initial Interview</h3>
              <p className="text-neutral-600">We'll review your application and conduct an initial interview</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-primary-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-primary-900 mb-2">Final Assessment</h3>
              <p className="text-neutral-600">Complete our assessment process and join our team</p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-primary-900 mb-4">Questions About Careers?</h2>
          <p className="text-neutral-600 mb-6">
            Contact our HR team for more information about opportunities at Maasai SightTrails.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:sighttrailsafaris@gmail.com"
              className="bg-primary-800 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Email HR Team
            </a>
            <a
              href="/contact"
              className="border-2 border-primary-800 text-primary-800 hover:bg-primary-800 hover:text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
